import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Edit, 
  Trash2, 
  Plus, 
  Search, 
  CheckCircle2,
  XCircle,
  MoreHorizontal,
  Package,
  Loader2
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Product, ProductCreate } from "@/types/product";
import { useWorkflows } from "@/hooks/useWorkflows";
import { useProductAttributes } from "@/hooks/useProductAttributes";
import { useProducts, useCreateProduct, useUpdateProduct, useBulkDeleteProducts } from "@/hooks/useProducts";
import { FieldType } from "@/types/productAttributes";
import { cn } from "@/lib/utils";

// Mock data
// Helper function to render specification fields based on field type
const renderSpecificationField = (
  attr: any, 
  value: any, 
  onChange: (value: any) => void,
  error?: string
) => {
  switch (attr.fieldType) {
    case FieldType.TEXT:
      return (
        <div className="space-y-1">
          <Input
            id={`spec-${attr.fieldName}`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`輸入${attr.displayName}`}
            className={cn(error && "border-red-500 focus-visible:ring-red-500")}
          />
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      );
    
    case FieldType.NUMBER:
      return (
        <div className="space-y-1">
          <Input
            id={`spec-${attr.fieldName}`}
            type="number"
            value={value || ""}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : "")}
            placeholder={`輸入${attr.displayName}`}
            className={cn(error && "border-red-500 focus-visible:ring-red-500")}
          />
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      );
    
    case FieldType.BOOLEAN:
      return (
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <Checkbox
              id={`spec-${attr.fieldName}`}
              checked={value || false}
              onCheckedChange={(checked) => onChange(checked)}
            />
            <Label htmlFor={`spec-${attr.fieldName}`} className="text-sm font-normal">
              {attr.displayName}
            </Label>
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      );
    
    case FieldType.SELECT:
      return (
        <div className="space-y-1">
          <Select
            value={value || ""}
            onValueChange={onChange}
          >
            <SelectTrigger className={cn(
              error && "border-red-500 focus:ring-red-500",
              !value && "text-muted-foreground",
              !attr.options?.length && "opacity-50 cursor-not-allowed"
            )}>
              <SelectValue placeholder={`選擇${attr.displayName}`} />
            </SelectTrigger>
            <SelectContent>
              {attr.options?.length ? (
                attr.options.map((option: string) => (
                  <SelectItem 
                    key={option} 
                    value={option}
                    className="hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 flex-shrink-0" />
                      <span>{option}</span>
                    </div>
                  </SelectItem>
                ))
              ) : (
                <div className="py-2 px-3 text-sm text-muted-foreground">
                  無可用選項
                </div>
              )}
            </SelectContent>
          </Select>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      );
    
    case FieldType.MULTISELECT:
      // For simplicity, using checkboxes for multiselect
      return (
        <div className="space-y-2">
          <div className="space-y-2">
            {attr.options?.map((option: string) => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`spec-${attr.fieldName}-${option}`}
                  checked={Array.isArray(value) && value.includes(option)}
                  onCheckedChange={(checked) => {
                    const currentValues = Array.isArray(value) ? value : [];
                    if (checked) {
                      onChange([...currentValues, option]);
                    } else {
                      onChange(currentValues.filter((v: string) => v !== option));
                    }
                  }}
                />
                <Label htmlFor={`spec-${attr.fieldName}-${option}`} className="text-sm font-normal">
                  {option}
                </Label>
              </div>
            ))}
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      );
    
    case FieldType.DATE:
      return (
        <div className="space-y-1">
          <Input
            id={`spec-${attr.fieldName}`}
            type="date"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className={cn(error && "border-red-500 focus-visible:ring-red-500")}
          />
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      );
    
    default:
      return (
        <div className="space-y-1">
          <Input
            id={`spec-${attr.fieldName}`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`輸入${attr.displayName}`}
            className={cn(error && "border-red-500 focus-visible:ring-red-500")}
          />
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      );
  }
};


export function ProductList() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  
  // Fetch data
  const { data: products = [], isLoading: productsLoading } = useProducts();
  const { data: workflows = [] } = useWorkflows();
  const { data: productAttributes = [] } = useProductAttributes();
  
  // Mutations
  const createProductMutation = useCreateProduct({
    onSuccess: () => {
      setIsAddDialogOpen(false);
      resetForm();
    }
  });
  
  const updateProductMutation = useUpdateProduct({
    onSuccess: () => {
      setIsEditDialogOpen(false);
      setEditingProduct(null);
      resetForm();
    }
  });
  
  const bulkDeleteMutation = useBulkDeleteProducts({
    onSuccess: () => {
      setSelectedProducts([]);
      setIsDeleteDialogOpen(false);
      setIsBulkDeleteDialogOpen(false);
      setEditingProduct(null);
    }
  });
  
  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  
  // Form states
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<ProductCreate>({
    code: "",
    name: "",
    specification: {},
    workflowId: "",
    isActive: true,
  });
  const [specificationValues, setSpecificationValues] = useState<Record<string, any>>({});
  
  // Validate form data
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Validate required fields
    if (!formData.code.trim()) {
      newErrors.code = "產品代碼為必填";
    }
    if (!formData.name.trim()) {
      newErrors.name = "產品名稱為必填";
    }
    
    // Validate required specification fields
    productAttributes.forEach(attr => {
      if (attr.isRequired) {
        const value = specificationValues[attr.fieldName];
        if (value === undefined || value === null || value === "" || 
            (Array.isArray(value) && value.length === 0)) {
          newErrors[`spec_${attr.fieldName}`] = `${attr.displayName}為必填`;
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const filteredProducts = products.filter(
    (product) =>
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (productsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="ml-2">載入產品列表中...</span>
      </div>
    );
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedProducts(filteredProducts.map(p => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (productId: string, checked: boolean) => {
    if (checked) {
      setSelectedProducts([...selectedProducts, productId]);
    } else {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    }
  };

  const handleAddProduct = () => {
    if (!validateForm()) {
      return;
    }
    
    // Build specification object with fieldName as key
    const specification: Record<string, any> = {};
    productAttributes.forEach(attr => {
      if (specificationValues[attr.fieldName] !== undefined) {
        specification[attr.fieldName] = specificationValues[attr.fieldName];
      }
    });

    const productData: ProductCreate = {
      code: formData.code.trim(),
      name: formData.name.trim(),
      specification: Object.keys(specification).length > 0 ? specification : undefined,
      workflowId: formData.workflowId || undefined,
      isActive: formData.isActive ?? true,
    };
    
    createProductMutation.mutate(productData);
  };

  const handleEditProduct = () => {
    if (!editingProduct) return;
    
    if (!validateForm()) {
      return;
    }
    
    // Build specification object with fieldName as key
    const specification: Record<string, any> = {};
    productAttributes.forEach(attr => {
      if (specificationValues[attr.fieldName] !== undefined) {
        specification[attr.fieldName] = specificationValues[attr.fieldName];
      }
    });

    const updateData = {
      code: formData.code.trim(),
      name: formData.name.trim(),
      specification: Object.keys(specification).length > 0 ? specification : undefined,
      workflowId: formData.workflowId || undefined,
      isActive: formData.isActive,
    };
    
    updateProductMutation.mutate({
      id: editingProduct.id,
      data: updateData
    });
  };

  const handleDeleteProduct = (productId: string) => {
    // Single delete using bulk delete endpoint with one ID
    bulkDeleteMutation.mutate({
      productIds: [productId]
    });
  };

  const handleBulkDelete = () => {
    // Bulk delete with multiple IDs
    if (selectedProducts.length === 0) return;
    
    bulkDeleteMutation.mutate({
      productIds: selectedProducts
    });
  };

  const openEditDialog = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      code: product.code,
      name: product.name,
      specification: product.specification,
      workflowId: product.workflowId || "",
      isActive: product.isActive,
    });
    
    // Map specification values - API converts snake_case to camelCase
    const specValues: Record<string, any> = {};
    if (product.specification) {
      productAttributes.forEach(attr => {
        // Convert snake_case fieldName to camelCase to match API response
        const camelCaseKey = attr.fieldName.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
        if (product.specification![camelCaseKey] !== undefined) {
          specValues[attr.fieldName] = product.specification![camelCaseKey];
        }
      });
    }
    
    setSpecificationValues(specValues);
    setErrors({});
    setIsEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      code: "",
      name: "",
      specification: {},
      workflowId: "",
      isActive: true,
    });
    setSpecificationValues({});
    setErrors({});
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-4">
        <div className="flex items-center gap-3 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="搜尋產品名稱或代碼..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selectedProducts.length > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setIsBulkDeleteDialogOpen(true)}
              disabled={bulkDeleteMutation.isPending}
            >
              {bulkDeleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  刪除中...
                </>
              ) : (
                `刪除選中 (${selectedProducts.length})`
              )}
            </Button>
          )}
          <Button onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            新增產品
          </Button>
        </div>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">
                <Checkbox
                  checked={
                    filteredProducts.length > 0 &&
                    selectedProducts.length === filteredProducts.length
                  }
                  onCheckedChange={handleSelectAll}
                />
              </TableHead>
              <TableHead>產品代碼</TableHead>
              <TableHead>產品名稱</TableHead>
              <TableHead>規格</TableHead>
              <TableHead>工作流程</TableHead>
              <TableHead>狀態</TableHead>
              <TableHead className="text-right">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredProducts.map((product) => (
              <TableRow key={product.id}>
                <TableCell>
                  <Checkbox
                    checked={selectedProducts.includes(product.id)}
                    onCheckedChange={(checked) => 
                      handleSelectProduct(product.id, checked as boolean)
                    }
                  />
                </TableCell>
                <TableCell className="font-medium">{product.code}</TableCell>
                <TableCell>{product.name}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {product.specification && 
                      Object.entries(product.specification).slice(0, 2).map(([key, value]) => (
                        <Badge key={key} variant="secondary" className="text-xs">
                          {key}: {String(value)}
                        </Badge>
                      ))
                    }
                    {product.specification && 
                     Object.keys(product.specification).length > 2 && (
                      <Badge variant="secondary" className="text-xs">
                        +{Object.keys(product.specification).length - 2}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {product.workflowId && product.workflowName ? (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      {product.workflowName}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground">未設定</span>
                  )}
                </TableCell>
                <TableCell>
                  {product.isActive ? (
                    <Badge className="bg-green-100 text-green-800">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      啟用
                    </Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-800">
                      <XCircle className="h-3 w-3 mr-1" />
                      停用
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditDialog(product)}>
                        <Edit className="h-4 w-4 mr-2" />
                        編輯
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => {
                          setEditingProduct(product);
                          setIsDeleteDialogOpen(true);
                        }}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        刪除
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        
        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">沒有找到產品</p>
          </div>
        )}
      </div>

      {/* Add Product Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新增產品</DialogTitle>
            <DialogDescription>
              輸入產品資訊以新增產品
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">
                產品代碼 <span className="text-red-500">*</span>
              </Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e) => {
                  setFormData({ ...formData, code: e.target.value });
                  if (errors.code) {
                    setErrors({ ...errors, code: "" });
                  }
                }}
                placeholder="PROD-XXX"
                className={cn(errors.code && "border-red-500 focus-visible:ring-red-500")}
              />
              {errors.code && <p className="text-xs text-red-500">{errors.code}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">
                產品名稱 <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (errors.name) {
                    setErrors({ ...errors, name: "" });
                  }
                }}
                placeholder="輸入產品名稱"
                className={cn(errors.name && "border-red-500 focus-visible:ring-red-500")}
              />
              {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="workflow">工作流程</Label>
              <Select
                value={formData.workflowId || "none"}
                onValueChange={(value) => setFormData({ ...formData, workflowId: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={workflows.length === 0 ? "目前沒有工作流程" : "選擇工作流程 (選填)"} />
                </SelectTrigger>
                <SelectContent>
                  {/* None option to allow deselection */}
                  <SelectItem 
                    value="none"
                    className="hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gray-300 flex-shrink-0" />
                      <span className="text-muted-foreground">無工作流程</span>
                    </div>
                  </SelectItem>
                  {workflows.map((workflow) => (
                    <SelectItem 
                      key={workflow.id} 
                      value={workflow.id}
                      className="hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-gradient-to-r from-green-400 to-green-600 flex-shrink-0" />
                        <span>{workflow.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {productAttributes.length > 0 && (
              <div className="space-y-4">
                <Label>產品規格</Label>
                {productAttributes.map((attr) => (
                  <div key={attr.id} className="space-y-2">
                    <Label htmlFor={`spec-${attr.fieldName}`}>
                      {attr.displayName}
                      {attr.isRequired && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    {renderSpecificationField(
                      attr, 
                      specificationValues[attr.fieldName], 
                      (value) => {
                        setSpecificationValues({ ...specificationValues, [attr.fieldName]: value });
                        const errorKey = `spec_${attr.fieldName}`;
                        if (errors[errorKey]) {
                          setErrors({ ...errors, [errorKey]: "" });
                        }
                      },
                      errors[`spec_${attr.fieldName}`]
                    )}
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) => 
                  setFormData({ ...formData, isActive: checked as boolean })
                }
              />
              <Label htmlFor="isActive">啟用產品</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              取消
            </Button>
            <Button 
              onClick={handleAddProduct}
              disabled={createProductMutation.isPending}
            >
              {createProductMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  創建中...
                </>
              ) : (
                "新增"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>編輯產品</DialogTitle>
            <DialogDescription>
              修改產品資訊
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-code">產品代碼</Label>
              <Input
                id="edit-code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="PROD-XXX"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-name">產品名稱</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="輸入產品名稱"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-workflow">工作流程</Label>
              <Select
                value={formData.workflowId || "none"}
                onValueChange={(value) => setFormData({ ...formData, workflowId: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={workflows.length === 0 ? "目前沒有工作流程" : "選擇工作流程 (選填)"} />
                </SelectTrigger>
                <SelectContent>
                  {/* None option to allow deselection */}
                  <SelectItem 
                    value="none"
                    className="hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gray-300 flex-shrink-0" />
                      <span className="text-muted-foreground">無工作流程</span>
                    </div>
                  </SelectItem>
                  {workflows.map((workflow) => (
                    <SelectItem 
                      key={workflow.id} 
                      value={workflow.id}
                      className="hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-gradient-to-r from-green-400 to-green-600 flex-shrink-0" />
                        <span>{workflow.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {productAttributes.length > 0 && (
              <div className="space-y-4">
                <Label>產品規格</Label>
                {productAttributes.map((attr) => (
                  <div key={attr.id} className="space-y-2">
                    <Label htmlFor={`edit-spec-${attr.fieldName}`}>
                      {attr.displayName}
                      {attr.isRequired && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    {renderSpecificationField(
                      attr, 
                      specificationValues[attr.fieldName], 
                      (value) => {
                        setSpecificationValues({ ...specificationValues, [attr.fieldName]: value });
                        const errorKey = `spec_${attr.fieldName}`;
                        if (errors[errorKey]) {
                          setErrors({ ...errors, [errorKey]: "" });
                        }
                      },
                      errors[`spec_${attr.fieldName}`]
                    )}
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit-isActive"
                checked={formData.isActive}
                onCheckedChange={(checked) => 
                  setFormData({ ...formData, isActive: checked as boolean })
                }
              />
              <Label htmlFor="edit-isActive">啟用產品</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              取消
            </Button>
            <Button 
              onClick={handleEditProduct}
              disabled={updateProductMutation.isPending}
            >
              {updateProductMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  更新中...
                </>
              ) : (
                "儲存"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>確認刪除</DialogTitle>
            <DialogDescription>
              確定要刪除產品「{editingProduct?.name}」嗎？此操作無法復原。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              取消
            </Button>
            <Button
              variant="destructive"
              onClick={() => editingProduct && handleDeleteProduct(editingProduct.id)}
              disabled={bulkDeleteMutation.isPending}
            >
              {bulkDeleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  刪除中...
                </>
              ) : (
                "確認刪除"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={isBulkDeleteDialogOpen} onOpenChange={setIsBulkDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>確認批量刪除</DialogTitle>
            <DialogDescription>
              確定要刪除選中的 {selectedProducts.length} 個產品嗎？此操作無法復原。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkDeleteDialogOpen(false)}>
              取消
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleBulkDelete}
              disabled={bulkDeleteMutation.isPending}
            >
              {bulkDeleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  刪除中...
                </>
              ) : (
                "確認刪除"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}